<template>
  <div
    v-if="shouldDisplayDataQualityButton"
    class="button-container"
  >
    <ReflectIconButton
      icon="icon-data-health"
      :size="iconSize"
      @click="isMenuOpen = true"
    />
    <i
      v-if="isCheckFailed"
      class="button-container__icon icon-error"
    />
  </div>
  <Teleport
    v-if="isMenuOpen"
    to="#action"
  >
    <SettingsDataQualityModal
      :date-range="dateRange"
      :data-quality-detail="{
        normalizationChecks: membersNormalizationChecks,
        dataHealthChecks: memberDataHealthChecks,
        sourceValueDimensions: sourceValueDimensions
      }"
      @close-modal="isMenuOpen = false"
    />
  </Teleport>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import type { Size } from '../Reflect/IconButton.vue'
import {
  getDataHealthCheckStatus,
  getDataHealthChecksFromMembers,
  getNormalizationChecksFromMembers,
  getNormalizationStatus,
  getSourceValueDimensionsFromMembers
} from '~/services/dataQuality'
import { useDataQualityStore } from '~/stores/dataQuality'
import {
  DataHealthCheckStatus,
  NormalizationCheckStatus,
  DataHealthCheck
} from '~/types/dataQuality'
import type { NormalizedFieldEntry } from '~/types/sourceValue'
import type { DateRange } from '~/types/timeDimension'
import type { Filter } from '~/types/analytics'
import { useCurrentUserStore } from '~/stores/currentUser'
import { buildObjectFromEntries } from '~/types/utils'

export default {
  name: 'DataQualityButton',
  props: {
    checkMode: {
      type: String as PropType<'trigger' | 'shared'>,
      required: true
    },
    dateRange: {
      type: Object as PropType<DateRange>,
      required: true
    },
    memberTitles: {
      type: Object as PropType<string[]>,
      required: true
    },
    filters: {
      type: Array as PropType<Filter[]>,
      required: true
    },
    iconSize: {
      type: String as PropType<Size>,
      default: 'medium'
    }
  },
  data(): {
    isMenuOpen: boolean
    membersNormalizationChecks: {
      [key in NormalizedFieldEntry]?: NormalizationCheckStatus | undefined
    }
    memberDataHealthChecks: {
      [key in DataHealthCheck]?: DataHealthCheckStatus | undefined
    }
  } {
    return {
      isMenuOpen: false,
      membersNormalizationChecks: {},
      memberDataHealthChecks: {}
    }
  },
  computed: {
    ...mapState(useDataQualityStore, [
      'normalizationChecks',
      'dataHealthChecks'
    ]),
    ...mapState(useCurrentUserStore, ['isCurrentUserAdmin']),
    dataQualityChecks() {
      return {
        normalizationChecks: this.normalizationChecks,
        dataHealthChecks: this.dataHealthChecks
      }
    },
    shouldDisplayDataQualityButton() {
      return (
        this.isCurrentUserAdmin &&
        (Object.values(this.membersNormalizationChecks).length > 0 ||
          Object.values(this.memberDataHealthChecks).length > 0 ||
          this.sourceValueDimensions.length > 0)
      )
    },
    isCheckFailed() {
      return (
        Object.values(this.membersNormalizationChecks).includes(
          NormalizationCheckStatus.FAIL
        ) ||
        Object.values(this.memberDataHealthChecks).includes(
          DataHealthCheckStatus.FAIL
        )
      )
    },
    sourceValueDimensions() {
      return getSourceValueDimensionsFromMembers(this.memberTitles)
    }
  },
  watch: {
    memberTitles: {
      immediate: true,
      handler() {
        if (this.checkMode !== 'trigger' || !this.isCurrentUserAdmin) {
          return
        }

        const normalizationChecks = getNormalizationChecksFromMembers(
          this.memberTitles
        )

        normalizationChecks.forEach(async normalizationCheck => {
          this.membersNormalizationChecks[normalizationCheck] =
            await getNormalizationStatus(normalizationCheck, this.dateRange)
        })

        const dataHealthChecks = getDataHealthChecksFromMembers(
          this.memberTitles
        )

        dataHealthChecks.forEach(async dataHealthCheck => {
          this.memberDataHealthChecks[dataHealthCheck] =
            await getDataHealthCheckStatus(
              dataHealthCheck,
              this.dateRange,
              this.filters
            )
        })
      }
    },
    dataQualityChecks: {
      immediate: true,
      handler() {
        if (this.checkMode !== 'shared') {
          return
        }

        this.membersNormalizationChecks = buildObjectFromEntries(
          getNormalizationChecksFromMembers(this.memberTitles).map(check => [
            check,
            this.normalizationChecks[check]
          ])
        )

        this.memberDataHealthChecks = buildObjectFromEntries(
          getDataHealthChecksFromMembers(this.memberTitles).map(check => [
            check,
            this.dataHealthChecks[check]
          ])
        )
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.button-container {
  position: relative;

  &__icon {
    @include font-icon;
    position: absolute;
    top: -6px;
    right: -6px;
    color: $color-error;
  }
}
</style>
